import { FunctionComponent, useState } from 'react'
import { useMutation } from '@apollo/client'
import styles from './createDaycareInput.module.scss'
import { CREATE_DAYCARE } from '@/modules/API/daycare/daycareHandler'
import Title from '@/components/_custom/Title/title'
import { DaycareType } from '@/types/Daycare'
import { SelectOptionsDeprecated } from '@/types/Select'
import moment from 'moment-timezone'
import { useTranslation } from 'react-i18next'
import UserInput from '@/components/_custom/UserInput/userInput'
import SelectUnique from '@/components/_custom/Selector/selectUnique/selectUnique'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import { useAuth } from '@/modules/context/AuthContext'
import ProfileAddress from '../account/profile/profileAddress/profileAddress'
import { useNotification } from '@/modules/context/NotificationContext.tsx'

const CreateDaycare: FunctionComponent = () => {
    const { t } = useTranslation()

    const { fetchCurrentDaycare, fetchDaycares } = useAuth()

    const navigate: NavigateFunction = useNavigate()

    const [daycareName, setDaycareName] = useState<string>('')
    const [phone, setPhone] = useState<string>('')
    const [validPhone, setValidPhone] = useState<boolean>(false)
    const [daycareType, setDaycareType] = useState<string>(DaycareType.daycare)
    const [managerName, setManagerName] = useState<string>('')
    const [email, setEmail] = useState<string>('')
    const [validEmail, setValidEmail] = useState<boolean>(false)
    const [address, setAddress] = useState<string>('')
    const [zip, setZip] = useState<string>('')
    const [city, setCity] = useState<string>('')
    const [country, setCountry] = useState<string>('')

    const { showNotificationError } = useNotification()

    const [createDaycare, { loading: createDaycareLoading }] = useMutation(CREATE_DAYCARE, {
        variables: {
            name: daycareName,
            phone: phone,
            type: daycareType,
            manager: managerName,
            email: email,
            address: address,
            zip: zip,
            city: city,
            country: country.toUpperCase(),
            timezone: moment.tz.guess()
        },
        onCompleted: async ({ createDaycare }: { createDaycare: number }): Promise<void> => {
            await fetchDaycares()
            fetchCurrentDaycare().then(() => {
                navigate('/daycare?daycare=' + createDaycare)
            })
        },
        onError: () => {
            showNotificationError(t('errorCreateDaycare'))
        }
    })

    function goBack(): void {
        navigate(-1)
    }

    const selectDaycareType: SelectOptionsDeprecated = [
        {
            options: [
                { value: DaycareType.daycare, name: t('daycare') },
                { value: DaycareType.nursery, name: t('nursery') },
                { value: DaycareType.toyLibrary, name: t('toyLibrary') },
                { value: DaycareType.microDaycare, name: t('microDaycare') },
                { value: DaycareType.multiHome, name: t('multiHome') },
                { value: DaycareType.recreationCenter, name: t('recreationCenter') },
                { value: DaycareType.playCenter, name: t('playCenter') },
                { value: DaycareType.rpe, name: t('rpe') },
                { value: DaycareType.laep, name: t('laep') },
                { value: DaycareType.other, name: t('other') }
            ]
        }
    ]

    const isButtonDisabled: boolean =
        createDaycareLoading ||
        daycareName === '' ||
        phone === '' ||
        managerName === '' ||
        email === '' ||
        zip === '' ||
        address === '' ||
        city === '' ||
        country === '' ||
        !validPhone ||
        !validEmail

    return (
        <div className={styles.PagePosition}>
            <div className={styles.CreateDaycarePlacement}>
                <div className={styles.Form}>
                    <div className={styles.Title}>
                        <Title text={t('daycareCreation')} />
                    </div>
                    <div className={styles.InputContainer}>
                        <div className={styles.Input}>
                            <SelectUnique
                                className={styles.DaycareTypeInput}
                                selectValue={daycareType}
                                setSelectValue={setDaycareType}
                                label={t('daycareType')}
                                selectOptions={selectDaycareType}
                                fullWidth
                            />
                        </div>
                        <div className={styles.Input}>
                            <UserInput
                                label={t('daycareName')}
                                value={daycareName}
                                setValue={setDaycareName}
                                type='text'
                                required
                                fullWidth
                                format={'pascal'}
                                className={styles.UserInput}
                            />
                            <UserInput
                                label={t('managerName')}
                                value={managerName}
                                setValue={setManagerName}
                                type='text'
                                required
                                fullWidth
                                format={'pascal'}
                                className={styles.UserInput}
                                unallowedCharacters={'num'}
                            />
                        </div>
                        <div className={styles.Input}>
                            <UserInput
                                label={t('email')}
                                value={email}
                                setValue={setEmail}
                                verify={'email'}
                                type='text'
                                unallowedCharacters={'email'}
                                setValid={setValidEmail}
                                required
                                fullWidth
                                className={styles.UserInput}
                            />
                            <UserInput
                                label={t('phone')}
                                value={phone}
                                setValue={setPhone}
                                type='text'
                                verify={'phone'}
                                required
                                fullWidth
                                format={'phone'}
                                setValid={setValidPhone}
                                className={styles.UserInput}
                            />
                        </div>
                    </div>
                    <div className={styles.Title}>
                        <Title text={t('address')} />
                    </div>
                    <div className={styles.InputContainer}>
                        <ProfileAddress
                            signUp
                            address={address}
                            setAddress={setAddress}
                            city={city}
                            setCity={setCity}
                            zip={zip}
                            setZip={setZip}
                            country={country}
                            setCountry={setCountry}
                        />
                    </div>
                    <div className={styles.ButtonContainer}>
                        <ButtonTextIcon onClick={goBack} className={styles.Button} type={'return'} />
                        <ButtonTextIcon
                            onClick={createDaycare}
                            className={styles.Button}
                            disabled={isButtonDisabled}
                            type={'confirm'}
                        />
                    </div>
                </div>
            </div>
        </div>
    )
}

export default CreateDaycare
